import {
  CollectionInvitationMutationResult,
  CollectionMembershipMutationResult,
  ListCollectionPeopleResult,
  parseCollectionInvitationMutationResult,
  parseCollectionMembershipMutationResult,
  parseListCollectionPeopleResult,
} from '@qkindex/contracts';
import { createOperationId } from '../../domain/operationId';
import {
  createCollectionInvitation,
  listCollectionPeople,
  resendCollectionInvitation,
  revokeCollectionInvitation,
  changeCollectionMemberRole,
  removeCollectionMember,
} from '../../firebase/functions';

export interface CollectionPeopleDependencies {
  createOperationId(): string;
  list(request: {
    collectionId: string;
    pageSize: number;
    memberCursor?: string;
    invitationCursor?: string;
  }): Promise<ListCollectionPeopleResult>;
  invite(request: {
    collectionId: string;
    operationId: string;
    email: string;
    role: 'administrator' | 'editor' | 'viewer';
  }): Promise<CollectionInvitationMutationResult>;
  resend(request: {
    collectionId: string;
    invitationId: string;
    operationId: string;
    expectedRevision: number;
  }): Promise<CollectionInvitationMutationResult>;
  revoke(request: {
    collectionId: string;
    invitationId: string;
    operationId: string;
    expectedRevision: number;
  }): Promise<CollectionInvitationMutationResult>;
  changeRole(request: {
    collectionId: string;
    targetUserId: string;
    operationId: string;
    expectedRevision: number;
    expectedMembershipVersion: number;
    role: 'administrator' | 'editor' | 'viewer';
  }): Promise<CollectionMembershipMutationResult>;
  removeMember(request: {
    collectionId: string;
    targetUserId: string;
    operationId: string;
    expectedRevision: number;
    expectedMembershipVersion: number;
    expectedRole: 'administrator' | 'editor' | 'viewer';
  }): Promise<CollectionMembershipMutationResult>;
}

const defaults = (): CollectionPeopleDependencies => ({
  createOperationId,
  list: async (request) =>
    parseListCollectionPeopleResult((await listCollectionPeople(request)).data),
  invite: async (request) =>
    parseCollectionInvitationMutationResult(
      (await createCollectionInvitation(request)).data,
    ),
  resend: async (request) =>
    parseCollectionInvitationMutationResult(
      (await resendCollectionInvitation(request)).data,
    ),
  revoke: async (request) =>
    parseCollectionInvitationMutationResult(
      (await revokeCollectionInvitation(request)).data,
    ),
  changeRole: async (request) =>
    parseCollectionMembershipMutationResult(
      (await changeCollectionMemberRole(request)).data,
    ),
  removeMember: async (request) =>
    parseCollectionMembershipMutationResult(
      (await removeCollectionMember(request)).data,
    ),
});

type PendingMutation = {
  key: string;
  operationId: string;
};

export const createCollectionPeopleController = (
  dependencies: CollectionPeopleDependencies = defaults(),
) => {
  let pendingInvite: PendingMutation | undefined;
  const pendingExisting = new Map<string, PendingMutation>();
  const pendingMembers = new Map<string, PendingMutation>();
  return {
    list(
      collectionId: string,
      cursors: { memberCursor?: string; invitationCursor?: string } = {},
    ) {
      return dependencies.list({ collectionId, pageSize: 10, ...cursors });
    },
    async invite(
      collectionId: string,
      rawEmail: string,
      role: 'administrator' | 'editor' | 'viewer',
    ) {
      const email = rawEmail.trim().toLowerCase();
      const key = `${collectionId}\0${email}\0${role}`;
      if (!pendingInvite || pendingInvite.key !== key) {
        pendingInvite = { key, operationId: dependencies.createOperationId() };
      }
      return dependencies.invite({
        collectionId,
        operationId: pendingInvite.operationId,
        email,
        role,
      });
    },
    acknowledgeInvite() {
      pendingInvite = undefined;
    },
    async resend(
      collectionId: string,
      invitationId: string,
      expectedRevision: number,
    ) {
      const key = `${collectionId}\0${invitationId}\0${expectedRevision}`;
      const mapKey = `resend:${invitationId}`;
      let pending = pendingExisting.get(mapKey);
      if (!pending || pending.key !== key) {
        pending = { key, operationId: dependencies.createOperationId() };
        pendingExisting.set(mapKey, pending);
      }
      return dependencies.resend({
        collectionId,
        invitationId,
        operationId: pending.operationId,
        expectedRevision,
      });
    },
    async revoke(
      collectionId: string,
      invitationId: string,
      expectedRevision: number,
    ) {
      const key = `${collectionId}\0${invitationId}\0${expectedRevision}`;
      const mapKey = `revoke:${invitationId}`;
      let pending = pendingExisting.get(mapKey);
      if (!pending || pending.key !== key) {
        pending = { key, operationId: dependencies.createOperationId() };
        pendingExisting.set(mapKey, pending);
      }
      return dependencies.revoke({
        collectionId,
        invitationId,
        operationId: pending.operationId,
        expectedRevision,
      });
    },
    acknowledge(invitationId: string) {
      pendingExisting.delete(`resend:${invitationId}`);
      pendingExisting.delete(`revoke:${invitationId}`);
    },
    async changeRole(
      collectionId: string,
      targetUserId: string,
      expectedRevision: number,
      expectedMembershipVersion: number,
      role: 'administrator' | 'editor' | 'viewer',
    ) {
      const key = `${collectionId}\0${targetUserId}\0${expectedRevision}\0${expectedMembershipVersion}\0${role}`;
      let pending = pendingMembers.get(`role:${targetUserId}`);
      if (!pending || pending.key !== key) {
        pending = { key, operationId: dependencies.createOperationId() };
        pendingMembers.set(`role:${targetUserId}`, pending);
      }
      return dependencies.changeRole({
        collectionId,
        targetUserId,
        operationId: pending.operationId,
        expectedRevision,
        expectedMembershipVersion,
        role,
      });
    },
    async removeMember(
      collectionId: string,
      targetUserId: string,
      expectedRevision: number,
      expectedMembershipVersion: number,
      expectedRole: 'administrator' | 'editor' | 'viewer',
    ) {
      const key = `${collectionId}\0${targetUserId}\0${expectedRevision}\0${expectedMembershipVersion}\0${expectedRole}`;
      let pending = pendingMembers.get(`remove:${targetUserId}`);
      if (!pending || pending.key !== key) {
        pending = { key, operationId: dependencies.createOperationId() };
        pendingMembers.set(`remove:${targetUserId}`, pending);
      }
      return dependencies.removeMember({
        collectionId,
        targetUserId,
        operationId: pending.operationId,
        expectedRevision,
        expectedMembershipVersion,
        expectedRole,
      });
    },
    acknowledgeMember(userId: string) {
      pendingMembers.delete(`role:${userId}`);
      pendingMembers.delete(`remove:${userId}`);
    },
    reset() {
      pendingInvite = undefined;
      pendingExisting.clear();
      pendingMembers.clear();
    },
  };
};

const nestedCode = (error: unknown, depth = 0): string | undefined => {
  if (!error || typeof error !== 'object' || depth > 3) return undefined;
  const value = error as {
    code?: unknown;
    details?: { code?: unknown };
    cause?: unknown;
  };
  if (typeof value.details?.code === 'string') return value.details.code;
  return (
    nestedCode(value.cause, depth + 1) ||
    (typeof value.code === 'string' ? value.code : undefined)
  );
};

export const collectionPeopleErrorMessage = (error: unknown): string => {
  const code = nestedCode(error);
  if (code?.includes('rate-limited'))
    return 'Too many invitation attempts. Try again later.';
  if (
    code?.includes('role-required') ||
    code?.includes('membership-required')
  ) {
    return 'Owner or administrator access is required. Only owners can manage administrators.';
  }
  if (code?.includes('operation-conflict')) {
    return 'The membership changed. Refresh and try again.';
  }
  if (code?.includes('collection-unavailable')) {
    return 'People changes are unavailable for this collection right now.';
  }
  if (code?.includes('invalid-request'))
    return 'Enter a valid email address and role.';
  return 'Unable to update people. Check your connection and try again.';
};
